/**
 * The saved diligence view of one property (`property-card-v8`, view
 * `diligence`): the card facts, the published resale/rental assessments, area
 * risk, market evidence, family fit, area composition and every retained local
 * source observation. Read-only; the server projects what the active experience
 * and the account may see, and omitted evidence stays absent here.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from './http'
import { useDealModelMode, useEntitlements } from './queries'
import { areaPotentialKey, contract, parsePersonalSettings, type JsonRecord } from './diligence'
import type { PersonalSettings } from './types'
import type { FloorAreaAssumption } from './floorAreaEvidence'
export type { FloorAreaAssumption } from './floorAreaEvidence'

/* ---------- shared card and market shapes ---------- */

export type PotentialBand = 'strong' | 'promising' | 'marginal' | 'weak'
export type CurrentMarketState = 'ready' | 'partial' | 'expired' | 'not_measured' | 'unavailable' | 'budget_blocked' | 'failed' | 'unknown'

export interface CardValuationEvidence {
  floorAreaSqm: number | null
  floorAreaAssumed?: boolean
  floorAreaAssumption?: FloorAreaAssumption | null
  gdvPence: number | null
  gdvLowerPence: number | null
  gdvUpperPence: number | null
  gdvConfidence: number | null
  gdvDisagreementRatio: number | null
  gdvReviewRequired: boolean
  gdvIndicative?: boolean
  gdvAtOrBelowAuctionPrice?: boolean
  floorAreaReviewRequired?: boolean
  unitPriceCheckPence?: number | null
  warnings?: string[]
  targetExitPence: number | null
  rentPcmPence?: number | null
}

export interface AuctionResult { status: string | null; highestBidPence: number | null; salePricePence: number | null; reserveMet: boolean | null; endedAt: string | null; sourceUrl: string | null; statusText: string | null }

export interface CalculationMeta { state: 'ready' | 'failed'; evaluatedAt: string; freshness?: 'current' | 'updating'; identitySourcesPending?: boolean }

export interface MarketPressure {
  basis: 'observed_stock_registered_flow_components'
  state: 'ready' | 'partial' | 'unavailable'
  availableCount: number | null
  agreedCount: number | null
  totalStatusCount: number | null
  agreedSharePercent: number | null
  registeredSalesPerMonth: number | null
  monthlyTurnoverPercent: number | null
  monthsOfInventory: number | null
  radiusMiles: number | null
  ageDays: number | null
  scopeKey: string | null
  scopeCompatible: boolean
  reasons: string[]
}

export interface CurrentMarket {
  state: CurrentMarketState | null
  effectiveAt: string | null
  totalForSale: number | null
  averageSalesPerMonth: number | null
  monthsOfInventory: number | null
  providerDaysOnMarket: number | null
  marketPressure?: MarketPressure | null
  askingPriceBasis: 'subject_equivalent' | 'raw_asking_price' | null
  askingPointsAnalysed: number | null
  askingRadiusMiles: number | null
  askingP10Pence: number | null
  askingP25Pence: number | null
  askingMedianPence: number | null
  askingP75Pence: number | null
  askingP90Pence: number | null
  askingPropertyTypeFilter: string | null
  askingBedroomsFilter: number | null
  targetAskingPercentile: number | null
}

export interface OfficialMarket {
  observationCount24m: number | null
  completedSalesPerMonth: number | null
  distributionMethod: string | null
  countsApproximate: boolean | null
  targetBandFraction: number | null
  targetBandSales24m: number | null
  targetBandSalesPerMonth: number | null
  salesAtOrAbove24m: number | null
  targetSoldPercentile: number | null
  priceBasis: 'subject_equivalent_exact_type_bedroom_size' | 'subject_equivalent_type_size_epc_reference_area' | 'subject_equivalent' | 'type_family_registered_total_price' | 'raw_sale_price' | null
  priceSource: 'propertydata_sold_prices_per_sqf' | 'property_market_hmlr_epc' | 'property_market_hmlr_registered' | 'local_hmlr_ppd' | null
  priceObservationCount: number | null
  priceRadiusMiles: number | null
  subjectFloorAreaSqm: number | null
  p25Pence: number | null
  medianPence: number | null
  p75Pence: number | null
}

export interface PotentialAssumption { key: string; defaultValue: number; unit: string; reason: string }

interface ScoreComponent { score: number | null; lower: number | null; upper: number | null; max: number | null; missing: string[] | null }

export interface ResalePotential {
  state: 'ready' | 'partial' | 'unknown' | null
  score: number | null
  scoreLower: number | null
  scoreUpper: number | null
  baseScore: number | null
  components: Record<'liquidity' | 'priceFit' | 'turnover' | 'affordability' | 'priceStability', ScoreComponent | null> | null
  band: PotentialBand | null
  confidence: number | null
  reasons: string[] | null
  assumptions?: PotentialAssumption[] | null
  assumedWeightPercent?: number | null
}

export interface RentalPotential {
  state: string | null
  score: number | null
  scoreLower: number | null
  scoreUpper: number | null
  baseScore: number | null
  band: PotentialBand | null
  confidence: number | null
  reasons: string[] | null
  explanation: string | null
  assumptions?: PotentialAssumption[] | null
  assumedWeightPercent?: number | null
  components: (Record<'liquidity' | 'speed' | 'affordability', (ScoreComponent & { explanation: string | null }) | null> & {
    privateRentedMarketDepth?: (ScoreComponent & { explanation: string | null; observedPercent: number | null; censusDate: string | null }) | null
  }) | null
}

export interface SelectedAdvert {
  portal: string
  listingId: string
  sourceUrl: string | null
  constructedUrl: string | null
  constructedUrlMethod: string | null
  displayAddress: string | null
  postcode: string | null
  propertyType: string | null
  bedrooms: number | null
  bathrooms: number | null
  internalAreaSqm: number | null
  pricePence: number | null
  rentMonthlyPence: number | null
  currency: string | null
  priceRole: string | null
  priceQualifier: string | null
  priceFrequency: string | null
  status: string | null
  statusBasis: string | null
  observedAt: string | null
  priceObservedAt: string | null
  statusObservedAt: string | null
  firstVisibleAt: string | null
  coordinateBasis: string | null
  distanceMetres: number | null
  measuredCentroidDistanceMetres: number | null
}

export interface SelectedAdvertGroup {
  state: 'ready' | 'thin' | 'indicative'
  selectionId: string
  releaseId: string | null
  candidateCount: number | null
  selectedCount: number
  omittedCandidateCount: number | null
  coverage: JsonRecord | null
  rows: SelectedAdvert[]
}

export interface RentalMarket {
  selectedRightmoveAdverts?: SelectedAdvertGroup | null
  scenario: string | null
  rentPcmPence: number | null
  rentLowerPcmPence: number | null
  rentUpperPcmPence: number | null
  rentSource: string | null
  rentBasis: string | null
  rentObservedAt: string | null
  onsBaselinePcmPence: number | null
  onsPeriod: string | null
  onsGeography: string | null
  onsGeographyType?: 'local_authority' | 'scottish_pipr_brma' | null
  onsBrmaName?: string | null
  onsBedroomBand: string | null
  totalForRent: number | null
  transactionsPerMonth: number | null
  turnoverPerMonthPercent: number | null
  monthsOfInventory: number | null
  daysOnMarket: number | null
  rentalDemandRating: string | null
  demandObservedAt: string | null
  valuationState: string | null
  demandState: string | null
  missingInputs: string[] | null
  reasons: string[] | null
  explanation: string | null
}

/* ---------- area context ---------- */

export interface AreaRiskComponent { score: number | null; max: number | null; scorePercent: number | null; source: string | null }

export interface AreaRisk {
  scoreLower?: number | null
  scoreUpper?: number | null
  assumedWeightPercent?: number | null
  assumptions?: PotentialAssumption[] | null
  state: 'ready' | 'partial' | 'unknown' | null
  score: number | null
  band: 'low' | 'moderate' | 'elevated' | 'severe' | null
  multiplier: number | null
  missingInputs: string[] | null
  components: Record<'income' | 'employment' | 'crime', AreaRiskComponent | null> | null
  reasons: string[] | null
}

export interface CompositionCategory { code: string; label: string; count: number; percent: number | null; parentCode: string | null; isAggregate: boolean }

export interface CompositionSection {
  lsoaCode: string | null
  lsoaName: string | null
  censusDate: string | null
  geographyMatchMethod: string | null
  sourceVersion: string | null
  sourceUrl: string | null
  denominator: number | null
  denominatorUnit: 'households' | 'residents' | null
  categories: CompositionCategory[] | null
}

export interface AreaComposition {
  tenure: CompositionSection | null
  demographics: (CompositionSection & { households: number | null; householdsWithChildren: number | null; householdsWithChildrenPercent: number | null }) | null
  ethnicity: CompositionSection | null
  religion: CompositionSection | null
}

export interface MarketIndicators {
  turnover: { state: 'ready' | 'unknown' | null; annualPercent: number | null; salesCount: number | null; dwellingCount: number | null; segment: string | null; msoaCode: string | null; windowStart: string | null; windowEndExclusive: string | null; stockEffectiveDate: string | null; registrationLagMonths: number | null; reasons: string[] | null } | null
  affordability: {
    state: 'ready' | 'partial' | 'unknown' | null; netAnnualIncomePence: number | null; incomeYearEnd: number | null; msoaCode: string | null; mortgageRatePercent: number | null; mortgageRatePeriod: string | null; loanToValuePercent: number | null; termYears: number | null; stressRateAdditionPercent: number | null
    resale: { monthlyPaymentPence: number | null; stressedMonthlyPaymentPence: number | null; incomeSharePercent: number | null; stressedIncomeSharePercent: number | null } | null
    rental?: { incomeSharePercent: number | null } | null
    reasons: string[] | null
  } | null
  priceStability: { state: 'ready' | 'unknown' | null; geographyCode: string | null; geographyName: string | null; segment: string | null; observedMonths: number | null; maxDrawdownPercent: number | null; series: Array<{ period: string; indexValue: number }> | null; reasons: string[] | null } | null
}

/* ---------- family and local sources ---------- */

export type FamilyBand = 'poor' | 'limited' | 'mixed' | 'good' | 'excellent'
export type FamilyFactorKey = 'schools' | 'crime' | 'outdoor' | 'childcare' | 'access'
export interface FamilyFactor { key: FamilyFactorKey; label: string; state: 'ready' | 'unavailable'; score: number | null; band: FamilyBand | null; summary: string; basis: JsonRecord }
export interface FamilyFit {
  scoreLower?: number | null
  scoreUpper?: number | null
  assumedWeightPercent?: number | null
  assumptions?: PotentialAssumption[] | null
  state: 'ready' | 'partial' | 'unavailable' | null
  score: number | null
  band: FamilyBand | null
  summary: string | null
  strengths: string[] | null
  concerns: string[] | null
  coverage: { available: number | null; total: number | null } | null
  factors: FamilyFactor[] | null
  methodVersion: string | null
  confidence: 'low' | 'medium' | null
  weights: Partial<Record<FamilyFactorKey, number | null>> | null
}
export interface FamilyPlace { id: string; name: string; type: string; distanceMiles: number; postcode?: string | null; evidence: JsonRecord }
export interface FamilyPlaces { radiusMiles: number; total: number; nearest: FamilyPlace[]; sourceAsOf: string | null; unlocatedRecords?: number | null }
export type ChildcareOwner = 'ofsted' | 'ciw' | 'care_inspectorate'
export interface NationalChildcarePlaces extends FamilyPlaces {
  redactedRecords?: number
  unlocatedPublicRecords?: number
  sourceObservationTime?: string | null
  sourceActiveUnlocatedProviders?: number
}
export interface NationalSchool {
  name: string
  distanceMiles: number
  postcodeCompact: string | null
  matchPrecision: string
  seedCode?: number
  schoolCode?: string
  centreType?: string
  phase?: string
}
export interface SchoolLocationContext {
  source: 'scottish_public_schools' | 'welsh_maintained_schools'
  sourceScope: 'open_publicly_funded_schools' | 'maintained_schools'
  coverageStatus: string | null
  snapshotDate: string | null
  searchRadiusMiles: number | null
  observedPublicSchoolsWithinRadius: number | null
  nearbyCountComplete: boolean
  nearestListComplete: boolean
  countsAreLowerBounds: boolean | null
  nearest: NationalSchool[]
  primaryDepartmentCount?: number | null
  secondaryDepartmentCount?: number | null
  specialDepartmentCount?: number | null
  phaseCounts?: Partial<Record<'nursery' | 'primary' | 'middle' | 'secondary' | 'special', number | null>> | null
}
export interface NationalSchoolLocations { sourceScope: SchoolLocationContext['sourceScope']; nearest: NationalSchool[] }
export interface ScottishSchoolInspectionSnapshot { sourceAsOf: string | null; counts: Record<string, number> }

export interface LocationAnchor { approximate: boolean | null; countryCode: string | null; geographyResolution: string; source: string }
export interface LocationSchool {
  ageServingBand?: 'primary_age' | 'secondary_age' | null
  distanceMiles: number
  name: string
  phase: string | null
  phaseBasis?: 'published_phase' | 'statutory_age_range' | 'establishment_name_hint' | null
  statutoryHighAge: number | null
  statutoryLowAge: number | null
  urn: string
}
export interface LocationSchools {
  counts: { independent: number | null; primary: number; radiusMiles: number; secondary: number; statutoryAgeRange?: number; nameHint?: number; unclassified?: number }
  nearest: LocationSchool[]
}
export interface TransportStop { atcoCode: string; distanceMiles: number; name: string; status: string | null; stopType: string }
export interface TransportStops { nearest: TransportStop[]; nearestRail: TransportStop | null }
export interface Broadband { gigabitPct: number | null; sfbbPct: number | null; unableDecentFixedOrFwaPct: number | null; snapshotDate: string }
export interface Planning {
  absenceIsClearance: boolean
  airQualityManagementArea: boolean; airQualityName: string | null
  ancientWoodland: boolean; ancientWoodlandName: string | null
  article4: boolean; article4Name: string | null
  conservationArea: boolean; conservationName: string | null
  greenBelt: boolean; greenBeltName: string | null
  listedBuilding: boolean; listedBuildingCount: number
  resolution: string
  treePreservationName: string | null; treePreservationZone: boolean
}

export interface SchoolAssessment { key: string; label: string; grade: string | null; date: string | null }
export interface SchoolInspection {
  framework: string
  inspectionUrn: string
  inspectionDate: string
  publicationDate: string | null
  overallGrade: string | null
  assessments: SchoolAssessment[]
  safeguarding: string | null
  safeguardingDate: string | null
  categoryOfConcern: string | null
  sourceAsOf: string
}
export interface SchoolInspectionRow {
  urn: string
  name: string
  phase: string | null
  independent: boolean | null
  coverageReason?: 'outside_england' | 'independent_school' | null
  distanceMiles: number
  postcode: string | null
  state: string
  inspection: SchoolInspection | null
  historical: boolean
  predecessor: boolean
  latestUngraded: { inspectionUrn: string; date: string; outcome: string | null } | null
  reportsUrl: string
  inspectionReportsUrl: string | null
}
export interface SchoolRatingCounts { total: number; legacy: Record<string, number>; reportCards: number; individualOnly: number }
export interface OfstedCurrent {
  radiusMiles: number
  total: number
  nearest: SchoolInspectionRow[]
  counts: { all?: SchoolRatingCounts; primary: { total: number }; secondary: { total: number }; independent: { total: number } } & Record<string, unknown>
  snapshotId: string
}

export type SchoolCoverageStatus = 'covered' | 'partial' | 'partial_source_coverage' | 'outside_coverage' | 'nation_unresolved' | 'anchor_unavailable' | 'source_unavailable'
interface SourceRecord<T> { current: T | null; anchor?: LocationAnchor | null; coverageStatus?: SchoolCoverageStatus | string | null }

export interface DeprivationSource { imdDecile: number | null; incomeDecile: number | null; employmentDecile: number | null; crimeDecile: number | null; lsoaCode: string | null; vintage: number | null; sourceUrl: string | null; accessedAt: string | null; geographyMatchMethod: string | null }
export interface WelshDeprivationSource { overallDecile: number | null; overallRank: number | null; incomeRank: number | null; employmentRank: number | null; communitySafetyRank: number | null; lsoaCode: string | null; vintage: number | null; sourceUrl: string | null; accessedAt: string | null; geographyMatchMethod: string | null }
export interface ScottishDeprivationSource { overallRank: number | null; incomeRank: number | null; employmentRank: number | null; crimeRank: number | null; dataZone2011Code: string | null; vintage: number | null; sourceUrl: string | null; accessedAt: string | null; matchPrecision: string | null }
export interface CrimeSource {
  comparison?: { ratePer1000: number | null } | null
  categories?: Array<{ category: string; count: number }>
  recordedCrimeCount12m?: number | null
  crimeRatePer1000: number | null
  lsoaCode: string | null
  periodStart: string | null
  periodEnd: string | null
  sourceUrl: string | null
  accessedAt: string | null
}

/* ---------- the response ---------- */

export interface AreaPotentialCard {
  valuation: CardValuationEvidence | null
  property: { uprn: string | null; propertyType: string | null; assetClass: string | null; tenure: string | null; bedrooms: number | null; bathrooms: number | null; latitude: number | null; longitude: number | null; address: { line1: string | null; line2: string | null; city: string | null; county: string | null; postcode: string | null } | null } | null
  listing: { marketedPricePence: number | null; auctionResult: AuctionResult | null; source: string | null; listingUrl: string | null; lotNumber: string | null; saleMethod: string | null; listingStatus: string | null; auctionDate: string | null } | null
  epc: { currentRating: string | null; potentialRating: string | null } | null
  lease: { statedYearsRemaining: number | null; statedAt: string | null } | null
}

export interface AreaPotentialDiligence {
  inputQuality?: { singleHomeState?: 'not_applicable' | 'needs_review' | 'not_contradicted' | null } | null
  market: { official: OfficialMarket | null; current: CurrentMarket | null; selectedRightmoveAdverts?: SelectedAdvertGroup | null } | null
  resalePotential: ResalePotential | null
  rentalPotential?: RentalPotential | null
  rentalMarket?: RentalMarket | null
  areaRisk: AreaRisk | null
  areaComposition: AreaComposition | null
  marketIndicators: MarketIndicators | null
  familyFit?: FamilyFit | null
  schoolLocationContext?: SchoolLocationContext | null
}

export interface AreaPotential {
  personalSettings: PersonalSettings | null
  propertyId: string
  calculation: CalculationMeta | null
  calculationState: 'ready' | 'failed' | 'pending'
  locationSourcesState: 'ready' | 'refreshing'
  card: AreaPotentialCard | null
  diligence: AreaPotentialDiligence | null
  englishImd: SourceRecord<DeprivationSource> | null
  welshWimd: SourceRecord<WelshDeprivationSource> | null
  scottishSimd: SourceRecord<ScottishDeprivationSource> | null
  onsGeography: SourceRecord<{ countryCode: string | null }> | null
  policeCrime: SourceRecord<CrimeSource> | null
  giasSchools: SourceRecord<LocationSchools> | null
  ofstedSchools: SourceRecord<OfstedCurrent> | null
  welshSchools: SourceRecord<NationalSchoolLocations> | null
  scottishPublicSchools: SourceRecord<NationalSchoolLocations> | null
  scottishSchoolInspections: SourceRecord<ScottishSchoolInspectionSnapshot> | null
  ciwChildcare: SourceRecord<NationalChildcarePlaces> | null
  careInspectorateChildcare: SourceRecord<NationalChildcarePlaces> | null
  naptanStops: SourceRecord<TransportStops> | null
  ofcomBroadband: SourceRecord<Broadband> | null
  planningData: SourceRecord<Planning> | null
  osGreenspace: SourceRecord<FamilyPlaces> | null
  ofstedChildcare: SourceRecord<FamilyPlaces> | null
  nhsServices: SourceRecord<FamilyPlaces> | null
  diagnostics: string[]
}

/* ---------- validation (ported from the workbench contract checks) ---------- */

const c = contract('area_potential_contract_mismatch')
const nationalCoverage = (status: unknown) => status === 'covered' || status === 'partial_source_coverage'

function validateNationalSources(data: JsonRecord): void {
  for (const [key, nation] of [['welshSchools', 'W92000004'], ['scottishPublicSchools', 'S92000003'], ['scottishSchoolInspections', 'S92000003'], ['ciwChildcare', 'W92000004'], ['careInspectorateChildcare', 'S92000003']] as const) {
    if (data[key] == null) continue // Older views and denied sources have no observation.
    const source = c.record(data[key], key)
    if (source.current == null) continue
    if (!nationalCoverage(source.coverageStatus)) c.fail(`${key} current observation has unavailable coverage`)
    const anchor = source.anchor == null ? null : c.record(source.anchor, `${key}.anchor`)
    if (anchor?.countryCode != null && anchor.countryCode !== nation) c.fail(`${key} has the wrong location nation`)
    const current = c.record(source.current, `${key}.current`)
    if (typeof current.sourceVersion !== 'string' || current.sourceVersion.length === 0) c.fail(`${key} source version missing`)
    if (!Array.isArray(current.nearest)) c.fail(`${key} nearest must be an array`)
    const nearest = current.nearest as unknown[]
    if (key === 'ciwChildcare' || key === 'careInspectorateChildcare') {
      c.count(current.total, `${key}.total`)
      c.number(current.radiusMiles, `${key}.radiusMiles`)
      if ((current.radiusMiles as number) <= 0 || current.coverageComplete !== false || typeof current.listComplete !== 'boolean') c.fail(`${key} coverage limits missing`)
      if (key === 'ciwChildcare' && (current.provisionScope !== 'registered_public_early_years_day_care' || current.operationStatusVerified !== false || current.registrationPresenceVerifiedByDirectory !== true || current.vacanciesKnown !== false)) c.fail('CIW registration-only scope missing')
      if (key === 'careInspectorateChildcare' && (current.scope !== 'registered_childcare_providers' || current.publicLocationOnly !== true || current.privateLocationsExcluded !== true || current.ageRange !== 'not_provided' || current.inspectionQuality !== 'not_provided' || current.capacity !== 'not_provided' || current.vacancies !== 'unknown')) c.fail('Scottish public childcare scope missing')
      const ids = new Set<string>()
      for (const value of nearest) {
        const place = c.record(value, `${key}.nearest`)
        const id = c.string(place.id, `${key}.id`)
        if (!id || ids.has(id)) c.fail(`${key} duplicate provider`)
        ids.add(id)
        c.string(place.name, `${key}.name`)
        const distance = c.number(place.distanceMiles, `${key}.distanceMiles`)
        if (distance < 0 || distance > (current.radiusMiles as number)) c.fail(`${key} provider outside radius`)
        const evidence = c.record(place.evidence, `${key}.evidence`)
        if (key === 'ciwChildcare' && !['Full Day Care', 'Sessional Day Care', 'Creche'].includes(String(place.type))) c.fail('CIW provider is outside public early-years scope')
        if (key === 'careInspectorateChildcare' && (place.type !== 'Day Care of Children' || evidence.status !== 'Active')) c.fail('Scottish childcare provider is private or inactive')
      }
      if (nearest.length > (current.total as number) || nearest.length > 20) c.fail(`${key} nearest count invalid`)
    } else if (key === 'scottishSchoolInspections') {
      if (current.inspectionQualityScored !== false || current.currentInspectionQuality !== null || current.snapshotCurrentness !== 'last_known_as_of_source_date' || current.latestInspectionCoverageVerified !== false || current.nurseryGradesIncluded !== false) c.fail('Scottish dated school inspection limits missing')
      for (const [name, count] of Object.entries(c.record(current.counts, 'Scottish dated inspection counts'))) c.count(count, `Scottish inspection ${name}`)
    } else {
      const scope = key === 'welshSchools' ? 'maintained_schools' : 'open_publicly_funded_schools'
      if (current.sourceScope !== scope || current.independentSchoolsIncluded !== false || (key === 'welshSchools' && current.inspectionQualityScored !== false)) c.fail(`${key} school directory scope missing`)
    }
  }
}

function validateSchoolLocationContext(value: unknown): void {
  if (value == null) return
  const context = c.record(value, 'schoolLocationContext')
  if (!['scottish_public_schools', 'welsh_maintained_schools'].includes(String(context.source))) c.fail('Unknown native school source')
  if (context.sourceScope !== (context.source === 'scottish_public_schools' ? 'open_publicly_funded_schools' : 'maintained_schools') || context.independentSchoolsIncluded !== false || context.inspectionQualityScored !== false || context.admissionEligibility !== 'unknown') c.fail('Native school context limits missing')
  c.count(context.observedPublicSchoolsWithinRadius, 'schoolLocationContext.count', true)
  c.count(context.unresolvedLocationCount, 'schoolLocationContext.unresolvedLocationCount', true)
  c.nullableNumber(context.searchRadiusMiles, 'schoolLocationContext.radius')
  for (const key of ['nearbyCountComplete', 'nearestListComplete']) if (typeof context[key] !== 'boolean') c.fail(`schoolLocationContext.${key} missing`)
  if (context.countsAreLowerBounds !== null && typeof context.countsAreLowerBounds !== 'boolean') c.fail('Native school count coverage missing')
  for (const key of ['primaryDepartmentCount', 'secondaryDepartmentCount', 'specialDepartmentCount']) if (context[key] !== undefined) c.count(context[key], `schoolLocationContext.${key}`, true)
  if (context.phaseCounts != null) for (const [key, count] of Object.entries(c.record(context.phaseCounts, 'native school phases'))) {
    if (!['nursery', 'primary', 'middle', 'secondary', 'special'].includes(key)) c.fail('Unknown native school phase')
    c.count(count, `native school phase ${key}`, true)
  }
  if (!Array.isArray(context.nearest) || context.nearest.length > 5) c.fail('Native school nearest list invalid')
  const ids = new Set<string>()
  for (const value of context.nearest as unknown[]) {
    const school = c.record(value, 'native school')
    c.string(school.name, 'native school name')
    if (c.number(school.distanceMiles, 'native school distance') < 0) c.fail('Negative native school distance')
    const scottish = context.source === 'scottish_public_schools'
    const id = scottish ? school.seedCode : school.schoolCode
    if (scottish) c.count(id, 'native SEED code')
    else if (typeof id !== 'string' || !id) c.fail('Native Welsh school code missing')
    if (!scottish && !['nursery', 'primary', 'middle', 'secondary', 'special'].includes(String(school.phase))) c.fail('Unsupported native Welsh phase')
    if (ids.has(String(id))) c.fail('Duplicate native school')
    ids.add(String(id))
    if (scottish ? !['uprn_exact', 'postcode_centroid'].includes(String(school.matchPrecision)) : school.matchPrecision !== 'source_grid_point') c.fail('Native school location precision unsupported')
  }
}

function validateMarketIndicators(value: unknown): void {
  const indicators = c.nullableRecord(value ?? null, 'marketIndicators')
  if (!indicators) return
  const turnover = c.nullableRecord(indicators.turnover ?? null, 'marketIndicators.turnover')
  if (turnover) {
    if (turnover.state !== null && turnover.state !== 'ready' && turnover.state !== 'unknown') c.fail('Invalid turnover state')
    for (const key of ['annualPercent', 'salesCount', 'dwellingCount', 'registrationLagMonths']) c.nullableNumber(turnover[key], `turnover.${key}`)
    if (turnover.reasons !== null) c.strings(turnover.reasons, 'turnover.reasons')
  }
  const affordability = c.nullableRecord(indicators.affordability ?? null, 'marketIndicators.affordability')
  if (affordability) {
    if (affordability.state !== null && !['ready', 'partial', 'unknown'].includes(String(affordability.state))) c.fail('Invalid affordability state')
    for (const key of ['netAnnualIncomePence', 'incomeYearEnd', 'mortgageRatePercent', 'loanToValuePercent', 'termYears', 'stressRateAdditionPercent']) c.nullableNumber(affordability[key], `affordability.${key}`)
    for (const key of ['msoaCode', 'mortgageRatePeriod']) c.nullableString(affordability[key], `affordability.${key}`)
    const resale = c.nullableRecord(affordability.resale ?? null, 'affordability.resale')
    if (resale) for (const key of ['monthlyPaymentPence', 'stressedMonthlyPaymentPence', 'incomeSharePercent', 'stressedIncomeSharePercent']) c.nullableNumber(resale[key], `affordability.resale.${key}`)
    // Home buyer and denied rental grants omit this branch during projection.
    if (Object.hasOwn(affordability, 'rental')) {
      const rental = c.nullableRecord(affordability.rental, 'affordability.rental')
      if (rental) c.nullableNumber(rental.incomeSharePercent, 'affordability.rental.incomeSharePercent')
    }
    if (affordability.reasons !== null) c.strings(affordability.reasons, 'affordability.reasons')
  }
  const stability = c.nullableRecord(indicators.priceStability ?? null, 'marketIndicators.priceStability')
  if (stability) {
    if (stability.state !== null && stability.state !== 'ready' && stability.state !== 'unknown') c.fail('Invalid price-stability state')
    c.nullableNumber(stability.observedMonths, 'priceStability.observedMonths')
    c.nullableNumber(stability.maxDrawdownPercent, 'priceStability.maxDrawdownPercent')
    if (stability.series !== null) {
      if (!Array.isArray(stability.series)) c.fail('priceStability.series must be an array')
      for (const [index, entry] of (stability.series as unknown[]).entries()) {
        const point = c.record(entry, `priceStability.series[${index}]`)
        c.string(point.period, `priceStability.series[${index}].period`)
        c.number(point.indexValue, `priceStability.series[${index}].indexValue`)
      }
    }
    if (stability.reasons !== null) c.strings(stability.reasons, 'priceStability.reasons')
  }
}

function validateAssumptions(value: unknown): void {
  if (value === null || value === undefined) return
  const potential = c.record(value, 'potential')
  if (potential.assumptions != null) {
    if (!Array.isArray(potential.assumptions) || potential.assumptions.length > 24) c.fail('Potential assumptions must be a bounded array')
    const seen = new Set<string>()
    for (const entry of potential.assumptions as unknown[]) {
      const item = c.record(entry, 'potential assumption')
      const key = c.string(item.key, 'assumption.key')
      if (seen.has(key)) c.fail('Potential assumption keys must be distinct')
      seen.add(key)
      c.number(item.defaultValue, 'assumption.defaultValue')
      c.string(item.unit, 'assumption.unit'); c.string(item.reason, 'assumption.reason')
    }
  }
  if (potential.assumedWeightPercent != null) {
    const weight = c.number(potential.assumedWeightPercent, 'assumedWeightPercent')
    if (weight < 0 || weight > 100) c.fail('Assumed market weight must be within 0–100%')
  }
}

function validateMarketPressure(value: unknown): void {
  if (value === undefined || value === null) return
  const pressure = c.record(value, 'marketPressure')
  if (pressure.basis !== 'observed_stock_registered_flow_components' || !['ready', 'partial', 'unavailable'].includes(String(pressure.state))) c.fail('Market pressure basis or state is unsupported')
  for (const key of ['availableCount', 'agreedCount', 'totalStatusCount']) {
    const count = c.nullableNumber(pressure[key], `marketPressure.${key}`)
    if (count !== null && (!Number.isSafeInteger(count) || count < 0)) c.fail('Market pressure counts must be nonnegative integers')
  }
  for (const key of ['agreedSharePercent', 'registeredSalesPerMonth', 'monthlyTurnoverPercent', 'monthsOfInventory', 'radiusMiles', 'ageDays']) {
    const measure = c.nullableNumber(pressure[key], `marketPressure.${key}`)
    if (measure !== null && (measure < 0 || (key === 'agreedSharePercent' && measure > 100))) c.fail('Market pressure measure is outside its range')
  }
  c.nullableString(pressure.scopeKey, 'marketPressure.scopeKey')
  c.boolean(pressure.scopeCompatible, 'marketPressure.scopeCompatible')
  c.strings(pressure.reasons, 'marketPressure.reasons')
  if (!pressure.scopeCompatible && (pressure.monthlyTurnoverPercent !== null || pressure.monthsOfInventory !== null)) c.fail('Incompatible market scopes cannot supply combined turnover or inventory')
}

function validateRental(diligence: JsonRecord): void {
  if (diligence.rentalPotential != null) {
    const potential = c.record(diligence.rentalPotential, 'rentalPotential')
    const score = c.nullableNumber(potential.score, 'rentalPotential.score')
    if (score !== null && (score < 0 || score > 100)) c.fail('Rental score is outside 0–100')
    if (potential.band !== null) c.literal(potential.band, ['strong', 'promising', 'marginal', 'weak'] as const, 'rentalPotential.band')
    for (const key of ['scoreLower', 'scoreUpper', 'confidence', 'baseScore']) c.nullableNumber(potential[key], `rentalPotential.${key}`)
    if (potential.reasons !== null) c.strings(potential.reasons, 'rentalPotential.reasons')
    if (potential.components != null) {
      const components = c.record(potential.components, 'rentalPotential.components')
      for (const key of ['liquidity', 'speed', 'affordability', 'privateRentedMarketDepth']) {
        if (components[key] == null) continue
        const component = c.record(components[key], `rentalPotential.components.${key}`)
        for (const field of ['score', 'lower', 'upper', 'max']) c.nullableNumber(component[field], `${key}.${field}`)
        if (component.missing !== null) c.strings(component.missing, `${key}.missing`)
      }
    }
  }
  if (diligence.rentalMarket == null) return
  const market = c.record(diligence.rentalMarket, 'rentalMarket')
  for (const key of ['rentPcmPence', 'rentLowerPcmPence', 'rentUpperPcmPence', 'onsBaselinePcmPence', 'totalForRent', 'transactionsPerMonth', 'turnoverPerMonthPercent', 'monthsOfInventory', 'daysOnMarket']) c.nullableNumber(market[key], `rentalMarket.${key}`)
  for (const key of ['scenario', 'rentSource', 'rentBasis', 'rentObservedAt', 'onsPeriod', 'onsGeography', 'onsBedroomBand', 'rentalDemandRating', 'demandObservedAt']) {
    if (!(key in market)) c.fail(`Missing rentalMarket.${key}`)
    c.nullableString(market[key], `rentalMarket.${key}`)
  }
  if ('onsGeographyType' in market && market.onsGeographyType !== null && market.onsGeographyType !== 'local_authority' && market.onsGeographyType !== 'scottish_pipr_brma') c.fail('Invalid rentalMarket.onsGeographyType')
  for (const key of ['missingInputs', 'reasons']) if (market[key] != null) c.strings(market[key], `rentalMarket.${key}`)
}

const ISO_TIME = /^(\d{4})-(\d{2})-(\d{2})T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+)?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/

/** Validate the frozen saved advert selection; rows are never reconstructed from a newer source. */
export function parseSelectedAdverts(value: unknown, name: string): SelectedAdvertGroup | null {
  if (value === undefined || value === null) return null // Older saved results have no local selection.
  const group = c.record(value, name)
  const identifier = (text: unknown, at: string) => { const id = c.string(text, at); if (!id.trim() || id.length > 256) c.fail(`${at} must be a bounded nonempty identifier`); return id }
  const time = (text: unknown, at: string) => {
    if (text === null) return
    const match = ISO_TIME.exec(c.string(text, at))
    if (!match || Number.isNaN(Date.parse(text as string))) c.fail(`${at} must be an ISO timestamp with timezone or null`)
  }
  c.literal(group.state, ['ready', 'thin', 'indicative'] as const, `${name}.state`)
  identifier(group.selectionId, `${name}.selectionId`)
  for (const key of ['selectionHash', 'releaseId', 'policyId', 'policyHash', 'subjectFingerprint']) if (group[key] !== null) identifier(group[key], `${name}.${key}`)
  if (group.coverage !== null) {
    const coverage = c.record(group.coverage, `${name}.coverage`)
    if ('complete' in coverage && coverage.complete !== null) c.boolean(coverage.complete, `${name}.coverage.complete`)
    if ('reasons' in coverage && coverage.reasons !== null) c.strings(coverage.reasons, `${name}.coverage.reasons`)
  }
  for (const key of ['candidateCount', 'omittedCandidateCount']) if (group[key] !== null) c.count(group[key], `${name}.${key}`)
  const selected = c.count(group.selectedCount, `${name}.selectedCount`) as number
  if (group.selectionComplete !== true || group.serializationComplete !== true) c.fail(`${name} must contain the complete saved selection`)
  if (!Array.isArray(group.rows) || group.rows.length > 100 || group.rows.length !== selected) c.fail(`${name}.selectedCount must match all bounded rows`)
  if (group.candidateCount !== null && (group.candidateCount as number) < selected) c.fail(`${name}.candidateCount is smaller than the selection`)
  if (group.candidateCount !== null && group.omittedCandidateCount !== null && group.candidateCount !== selected + (group.omittedCandidateCount as number)) c.fail(`${name} candidate counts are inconsistent`)
  const ids = new Set<string>()
  for (const [index, entry] of (group.rows as unknown[]).entries()) {
    const at = `${name}.rows[${index}]`
    const row = c.record(entry, at)
    const id = `${identifier(row.portal, `${at}.portal`)}:${identifier(row.listingId, `${at}.listingId`)}`
    if (ids.has(id)) c.fail(`${at} repeats a selected advert`)
    ids.add(id)
    for (const key of ['sourceUrl', 'constructedUrl', 'constructedUrlMethod', 'displayAddress', 'postcode', 'propertyType', 'currency', 'priceRole', 'priceQualifier', 'priceFrequency', 'status', 'statusBasis', 'coordinateBasis', 'firstVisibleAt']) c.nullableString(row[key], `${at}.${key}`)
    for (const key of ['bedrooms', 'bathrooms']) if (row[key] !== null) c.count(row[key], `${at}.${key}`)
    for (const key of ['pricePence', 'rentMonthlyPence', 'internalAreaSqm']) { const amount = c.nullableNumber(row[key], `${at}.${key}`); if (amount !== null && amount <= 0) c.fail(`${at}.${key} must be positive or null`) }
    for (const key of ['distanceMetres', 'measuredCentroidDistanceMetres']) { const distance = c.nullableNumber(row[key], `${at}.${key}`); if (distance !== null && distance < 0) c.fail(`${at}.${key} must be nonnegative or null`) }
    for (const key of ['observedAt', 'priceObservedAt', 'statusObservedAt']) time(row[key], `${at}.${key}`)
  }
  return group as unknown as SelectedAdvertGroup
}

function parseCalculation(value: unknown): CalculationMeta {
  const calculation = c.record(value, 'calculation')
  return {
    state: c.literal(calculation.state, ['ready', 'failed'] as const, 'calculation.state'),
    evaluatedAt: c.string(calculation.evaluatedAt, 'calculation.evaluatedAt'),
    freshness: calculation.freshness === undefined ? undefined : c.literal(calculation.freshness, ['current', 'updating'] as const, 'calculation.freshness'),
    identitySourcesPending: calculation.identitySourcesPending === undefined ? undefined : calculation.identitySourcesPending === true,
  }
}

const source = <T,>(value: unknown): SourceRecord<T> | null => (value == null ? null : value as SourceRecord<T>)

export function parseAreaPotential(value: unknown, expectedPropertyId: string): AreaPotential {
  const data = c.record(c.record(value, 'area potential envelope').data, 'area potential data')
  const propertyId = c.string(data.propertyId, 'propertyId')
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(propertyId)) c.fail('propertyId must be a canonical lowercase UUID')
  if (propertyId !== expectedPropertyId) c.fail('area potential propertyId does not match the requested property')
  if (data.contractVersion !== 'property-card-v8') c.fail('area potential contract must be property-card-v8')
  if (data.view !== 'diligence') c.fail('Expected the diligence view')
  const calculationState = c.literal(data.calculationState, ['ready', 'failed', 'pending'] as const, 'calculationState')
  if (data.locationSourcesState !== undefined && !['ready', 'refreshing'].includes(String(data.locationSourcesState))) c.fail('Invalid location sources state')
  if ((data.calculation === null) !== (calculationState === 'pending')) c.fail('Pending calculation must have null metadata')
  const card = c.nullableRecord(data.card ?? null, 'area potential card')
  let diligence: AreaPotentialDiligence | null = null
  if (data.diligence != null) {
    const raw = c.record(data.diligence, 'diligence')
    if (raw.market != null) {
      const market = c.record(raw.market, 'market')
      if (market.current != null) validateMarketPressure(c.record(market.current, 'market.current').marketPressure)
    }
    for (const key of ['resalePotential', 'rentalPotential', 'areaRisk', 'familyFit']) validateAssumptions(raw[key])
    validateRental(raw)
    validateSchoolLocationContext(raw.schoolLocationContext)
    validateMarketIndicators(raw.marketIndicators)
    const market = raw.market == null ? null : c.record(raw.market, 'market')
    const rentalMarket = raw.rentalMarket == null ? null : c.record(raw.rentalMarket, 'rentalMarket')
    diligence = {
      ...(raw as unknown as AreaPotentialDiligence),
      market: market ? { ...(market as unknown as NonNullable<AreaPotentialDiligence['market']>), selectedRightmoveAdverts: parseSelectedAdverts(market.selectedRightmoveAdverts, 'market.selectedRightmoveAdverts') } : null,
      rentalMarket: rentalMarket ? { ...(rentalMarket as unknown as RentalMarket), selectedRightmoveAdverts: parseSelectedAdverts(rentalMarket.selectedRightmoveAdverts, 'rentalMarket.selectedRightmoveAdverts') } : (raw.rentalMarket === undefined ? undefined : null),
      schoolLocationContext: (raw.schoolLocationContext as SchoolLocationContext | undefined) ?? null,
    }
  }
  validateNationalSources(data)
  return {
    personalSettings: data.personalSettings == null ? null : parsePersonalSettings(data.personalSettings, propertyId),
    propertyId,
    calculation: data.calculation === null ? null : parseCalculation(data.calculation),
    calculationState,
    locationSourcesState: data.locationSourcesState === 'refreshing' ? 'refreshing' : 'ready',
    card: card as AreaPotentialCard | null,
    diligence,
    englishImd: source(data.englishImd),
    welshWimd: source(data.welshWimd),
    scottishSimd: source(data.scottishSimd),
    onsGeography: source(data.onsGeography),
    policeCrime: source(data.policeCrime),
    giasSchools: source(data.giasSchools),
    ofstedSchools: source(data.ofstedSchools),
    welshSchools: source(data.welshSchools),
    scottishPublicSchools: source(data.scottishPublicSchools),
    scottishSchoolInspections: source(data.scottishSchoolInspections),
    ciwChildcare: source(data.ciwChildcare),
    careInspectorateChildcare: source(data.careInspectorateChildcare),
    naptanStops: source(data.naptanStops),
    ofcomBroadband: source(data.ofcomBroadband),
    planningData: source(data.planningData),
    osGreenspace: source(data.osGreenspace),
    ofstedChildcare: source(data.ofstedChildcare),
    nhsServices: source(data.nhsServices),
    diagnostics: c.strings(data.diagnostics, 'diagnostics'),
  }
}

/* ---------- selectors (source/nation fences, as the workbench applied them) ---------- */

function sourceNationMatches(record: SourceRecord<unknown> | null | undefined, response: AreaPotential, nation: string): boolean {
  const country = response.onsGeography?.current?.countryCode
  const anchorCountry = record?.anchor?.countryCode
  return (country === nation || anchorCountry === nation) && (!country || country === nation) && (!anchorCountry || anchorCountry === nation)
}

export function selectFamilyChildcare(response: AreaPotential | null): { places: FamilyPlaces | NationalChildcarePlaces; owner: ChildcareOwner } | null {
  if (!response || response.locationSourcesState === 'refreshing') return null
  for (const [key, nation, owner] of [['ciwChildcare', 'W92000004', 'ciw'], ['careInspectorateChildcare', 'S92000003', 'care_inspectorate']] as const) {
    const record = response[key]
    if (record?.current && nationalCoverage(record.coverageStatus) && sourceNationMatches(record, response, nation)) return { places: record.current, owner }
  }
  const english = response.ofstedChildcare
  return english?.current && sourceNationMatches(english, response, 'E92000001') ? { places: english.current, owner: 'ofsted' } : null
}

export function selectSchoolLocationContext(response: AreaPotential | null): SchoolLocationContext | null {
  if (!response || response.locationSourcesState === 'refreshing') return null
  const context = response.diligence?.schoolLocationContext
  if (!context || !nationalCoverage(context.coverageStatus)) return null
  const scottish = context.source === 'scottish_public_schools'
  const record = scottish ? response.scottishPublicSchools : response.welshSchools
  return record?.current && record.current.sourceScope === context.sourceScope && nationalCoverage(record.coverageStatus) && sourceNationMatches(record, response, scottish ? 'S92000003' : 'W92000004') ? context : null
}

/* ---------- the hook ---------- */

/** One saved diligence read per property and experience; never triggers a calculation. */
export function useAreaPotential(propertyId: string, enabled = true) {
  const mode = useDealModelMode()
  const entitlements = useEntitlements()
  const projection = { capabilities: entitlements.data?.capabilities, access: entitlements.data?.access, trialLimited: entitlements.data?.trialLimited }
  return useQuery({
    queryKey: [...areaPotentialKey(propertyId), mode.data?.activeMode ?? null, mode.data?.preferenceRevision ?? null, projection],
    queryFn: async ({ signal }) => parseAreaPotential(await api('areaPotential', { params: { propertyId }, signal }), propertyId),
    enabled: enabled && propertyId.length > 0 && mode.isSuccess && entitlements.isSuccess && mode.data.activeMode !== null,
    retry: false,
    refetchOnWindowFocus: false,
  })
}
